// Core Firestore Service Operations
import {
    collection,
    doc,
    getDoc,
    getDocs,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp,
    Timestamp,
    onSnapshot,
    QueryConstraint,
    arrayRemove,
    arrayUnion,
    writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, auth } from '../config/firebase';
import { Trip, Country, Region, MapData } from '../models/index';
import { CloneOptions, cloneOptionsPayload } from '../models/cloneOptions';
import NotificationService from './notification.service';

export class TripService {
    private static COLLECTION = 'trips';

    private static readonly DATE_FIELDS = [
        'startDate',
        'endDate',
        'createdAt',
        'updatedAt',
        'lastModifiedAt',
    ];

    /**
     * Coerce whatever Firestore holds in a date field into a real `Date`.
     *
     * Trip dates are not stored uniformly: this app writes Firestore
     * Timestamps, while the Flutter app writes ISO-8601 strings
     * (`lib/models/trip.dart` → `startDate.toIso8601String()`). Handling only
     * Timestamps left `Trip.startDate` a string for any trip last written by
     * mobile, so anything trusting the declared `Date` type — e.g. sorting by
     * `startDate.getTime()` — threw at runtime.
     */
    private static toDate(value: any): Date | undefined {
        if (value == null) return undefined;
        if (value instanceof Date) return isNaN(value.getTime()) ? undefined : value;
        if (value instanceof Timestamp) return value.toDate();
        // A Timestamp from a different copy of the SDK fails `instanceof`.
        if (typeof value.toDate === 'function') return this.toDate(value.toDate());
        // A Timestamp that went through JSON (e.g. an exported/imported trip).
        if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
        if (typeof value._seconds === 'number') return new Date(value._seconds * 1000);
        if (typeof value === 'string' || typeof value === 'number') {
            const parsed = new Date(value);
            return isNaN(parsed.getTime()) ? undefined : parsed;
        }
        return undefined;
    }

    // Convert every stored date representation to a Date
    private static convertTimestamps(data: any): any {
        const converted = { ...data };
        for (const field of this.DATE_FIELDS) {
            if (!(field in data)) continue;
            const date = this.toDate(data[field]);
            // Drop a value we can't parse rather than leave one that only
            // pretends to be a Date and blows up at the first `.getTime()`.
            if (date) converted[field] = date;
            else delete converted[field];
        }
        return converted;
    }

    /**
     * Apply runtime defaults to raw Firestore data so that fields added after a
     * trip was first created are never `undefined` in the app.
     *
     * Public because the Trip Market pages read published trips outside the
     * normal member-scoped queries and need the identical normalization.
     */
    static normalizeTripData(id: string, raw: any): Trip {
        return {
            defaultCurrency: 'USD',
            currencyRates: {},
            participantIds: [],
            sharedToUserIds: [],
            countries: [],
            regions: [],
            highlightImages: [],
            mainImageIndex: 0,
            mapNeedsUpdate: false,
            // Raw Firestore data (with timestamps already converted) wins over the defaults above.
            ...this.convertTimestamps(raw),
            id,
        } as Trip;
    }

    // Get all trips for a user (owned, participating, or viewing)
    static async getUserTrips(userId: string): Promise<Trip[]> {
        try {
            console.log('🔍 [TripService] Getting trips for user:', userId);
            const trips: Trip[] = [];

            // Get owned trips
            console.log('🔍 [TripService] Querying owned trips...');
            const ownedQuery = query(
                collection(db, this.COLLECTION),
                where('ownerId', '==', userId)
            );
            const ownedSnapshot = await getDocs(ownedQuery);
            console.log('✅ [TripService] Found', ownedSnapshot.size, 'owned trips');
            ownedSnapshot.forEach((doc) => {
                trips.push(this.normalizeTripData(doc.id, doc.data()));
            });

            // Get participant trips
            console.log('🔍 [TripService] Querying participant trips...');
            const participantQuery = query(
                collection(db, this.COLLECTION),
                where('participantIds', 'array-contains', userId)
            );
            const participantSnapshot = await getDocs(participantQuery);
            console.log('✅ [TripService] Found', participantSnapshot.size, 'participant trips');
            participantSnapshot.forEach((doc) => {
                const tripData = this.normalizeTripData(doc.id, doc.data());
                // Avoid duplicates
                if (!trips.find((t) => t.id === tripData.id)) {
                    trips.push(tripData);
                }
            });

            // Get shared trips (viewer)
            console.log('🔍 [TripService] Querying shared/viewer trips...');
            const sharedQuery = query(
                collection(db, this.COLLECTION),
                where('sharedToUserIds', 'array-contains', userId)
            );
            const sharedSnapshot = await getDocs(sharedQuery);
            console.log('✅ [TripService] Found', sharedSnapshot.size, 'shared trips');
            sharedSnapshot.forEach((doc) => {
                const tripData = this.normalizeTripData(doc.id, doc.data());
                // Avoid duplicates
                if (!trips.find((t) => t.id === tripData.id)) {
                    trips.push(tripData);
                }
            });

            console.log('✅ [TripService] Total trips found:', trips.length);
            return trips;
        } catch (error: any) {
            if (error?.code === 'permission-denied') {
                console.warn('⚠️ [TripService] Firestore permission denied — auth token not ready yet');
            } else {
                console.error('❌ [TripService] Error getting user trips:', error);
            }
            throw error;
        }
    }

    // Subscribe to real-time trip updates
    static subscribeToUserTrips(
        userId: string,
        callback: (trips: Trip[]) => void
    ): () => void {
        console.log('🔍 [TripService] Setting up real-time subscriptions for user:', userId);
        const trips = new Map<string, Trip>();
        // Debounce callback so rapid-fire from multiple simultaneous snapshots
        // (e.g. owner also in participantIds) never produce duplicates in one render.
        let flushTimer: ReturnType<typeof setTimeout> | null = null;
        const flushCallback = () => {
            if (flushTimer) clearTimeout(flushTimer);
            flushTimer = setTimeout(() => {
                callback(Array.from(trips.values()));
            }, 0);
        };

        // Subscribe to owned trips
        const ownedQuery = query(
            collection(db, this.COLLECTION),
            where('ownerId', '==', userId)
        );
        const unsubscribeOwned = onSnapshot(
            ownedQuery,
            (snapshot) => {
                console.log('📡 [TripService] Owned trips update:', snapshot.size, 'trips');
                snapshot.docChanges().forEach((change) => {
                    const tripData = {
                        id: change.doc.id,
                        ...this.convertTimestamps(change.doc.data()),
                    } as Trip;

                    if (change.type === 'removed') {
                        trips.delete(change.doc.id);
                    } else {
                        trips.set(change.doc.id, tripData);
                    }
                });
                flushCallback();
            },
            (error) => {
                console.error('❌ [TripService] Error in owned trips subscription:', error);
            }
        );

        // Subscribe to participant trips
        const participantQuery = query(
            collection(db, this.COLLECTION),
            where('participantIds', 'array-contains', userId)
        );
        const unsubscribeParticipant = onSnapshot(
            participantQuery,
            (snapshot) => {
                console.log('📡 [TripService] Participant trips update:', snapshot.size, 'trips');
                snapshot.docChanges().forEach((change) => {
                    const tripData = {
                        id: change.doc.id,
                        ...this.convertTimestamps(change.doc.data()),
                    } as Trip;

                    if (change.type === 'removed') {
                        trips.delete(change.doc.id);
                    } else {
                        trips.set(change.doc.id, tripData);
                    }
                });
                flushCallback();
            },
            (error) => {
                console.error('❌ [TripService] Error in participant trips subscription:', error);
            }
        );

        // Subscribe to shared trips
        const sharedQuery = query(
            collection(db, this.COLLECTION),
            where('sharedToUserIds', 'array-contains', userId)
        );
        const unsubscribeShared = onSnapshot(
            sharedQuery,
            (snapshot) => {
                console.log('📡 [TripService] Shared trips update:', snapshot.size, 'trips');
                snapshot.docChanges().forEach((change) => {
                    const tripData = {
                        id: change.doc.id,
                        ...this.convertTimestamps(change.doc.data()),
                    } as Trip;

                    if (change.type === 'removed') {
                        trips.delete(change.doc.id);
                    } else {
                        trips.set(change.doc.id, tripData);
                    }
                });
                flushCallback();
            },
            (error) => {
                console.error('❌ [TripService] Error in shared trips subscription:', error);
            }
        );

        // Return combined unsubscribe function
        return () => {
            console.log('🔌 [TripService] Unsubscribing from all trip subscriptions');
            if (flushTimer) clearTimeout(flushTimer);
            unsubscribeOwned();
            unsubscribeParticipant();
            unsubscribeShared();
        };
    }

    // Get a single trip by ID
    static async getTripById(tripId: string): Promise<Trip | null> {
        try {
            const tripDoc = await getDoc(doc(db, this.COLLECTION, tripId));
            if (tripDoc.exists()) {
                return {
                    id: tripDoc.id,
                    ...this.convertTimestamps(tripDoc.data()),
                } as Trip;
            }
            return null;
        } catch (error) {
            console.error('Error getting trip:', error);
            throw error;
        }
    }

    // Alias for getTripById for backward compatibility
    static async getTrip(tripId: string): Promise<Trip | null> {
        return this.getTripById(tripId);
    }

    // Subscribe to a single trip
    static subscribeToTrip(
        tripId: string,
        callback: (trip: Trip | null) => void,
        onError?: (error: Error & { code?: string }) => void
    ): () => void {
        return onSnapshot(doc(db, this.COLLECTION, tripId), (snapshot) => {
            if (snapshot.exists()) {
                callback(this.normalizeTripData(snapshot.id, snapshot.data()));
            } else {
                callback(null);
            }
        }, (error) => {
            console.error('Error in trip subscription:', error);
            if (onError) {
                onError(error as Error & { code?: string });
            } else {
                // Legacy fallback: notify caller so the UI can exit the loading state.
                callback(null);
            }
        });
    }

    // Create a new trip
    static async createTrip(trip: Omit<Trip, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
        try {
            const tripRef = doc(collection(db, this.COLLECTION));
            const tripData = {
                ...trip,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                // Admin "last changed" sort key; also bumped by the onTripContentWrite
                // Cloud Function when content subcollections change.
                lastModifiedAt: serverTimestamp(),
            };
            await setDoc(tripRef, tripData);
            return tripRef.id;
        } catch (error) {
            console.error('Error creating trip:', error);
            throw error;
        }
    }

    // Update a trip
    static async updateTrip(tripId: string, updates: Partial<Trip>): Promise<void> {
        try {
            const tripRef = doc(db, this.COLLECTION, tripId);
            await updateDoc(tripRef, {
                ...updates,
                updatedAt: serverTimestamp(),
                lastModifiedAt: serverTimestamp(),
            });
        } catch (error) {
            console.error('Error updating trip:', error);
            throw error;
        }
    }

    // Delete a trip and all its subcollections
    static async deleteTrip(tripId: string): Promise<void> {
        try {
            // Delete all known subcollections first
            const subcollections = [
                'activities',
                'accommodations',
                'flightTickets',
                'carRentals',
                'expenses',
                'documents',
                'checklist',
                'checklist_metadata',
                'checklists',
                'equipment',
                'equipment_metadata',
                'equipments',
                'helpfulLinks',
                'images',
                'metrics_counters',
            ];

            for (const sub of subcollections) {
                const subRef = collection(db, this.COLLECTION, tripId, sub);
                const snapshot = await getDocs(subRef);
                if (snapshot.empty) continue;

                // Batch delete in groups of 500 (Firestore limit)
                const batchSize = 500;
                for (let i = 0; i < snapshot.docs.length; i += batchSize) {
                    const batch = writeBatch(db);
                    const chunk = snapshot.docs.slice(i, i + batchSize);
                    chunk.forEach((d) => batch.delete(d.ref));
                    await batch.commit();
                }
            }

            // Delete the trip document itself
            await deleteDoc(doc(db, this.COLLECTION, tripId));
        } catch (error) {
            console.error('Error deleting trip:', error);
            throw error;
        }
    }

    // Get user role in a trip
    static async getUserRole(tripId: string, userId: string): Promise<'owner' | 'participant' | 'viewer' | 'none'> {
        try {
            const trip = await this.getTripById(tripId);
            if (!trip) return 'none';

            if (trip.ownerId === userId) return 'owner';
            if (trip.participantIds.includes(userId)) return 'participant';
            if (trip.sharedToUserIds.includes(userId)) return 'viewer';
            return 'none';
        } catch (error) {
            console.error('Error getting user role:', error);
            return 'none';
        }
    }

    // Add participant to trip
    static async addParticipant(tripId: string, userId: string): Promise<void> {
        try {
            console.log('👥 [TripService] Adding participant:', userId, 'to trip:', tripId);
            const trip = await this.getTripById(tripId);
            if (!trip) throw new Error('Trip not found');

            // Check if user is already a participant
            if (trip.participantIds.includes(userId)) {
                console.log('⚠️ User is already a participant');
                return;
            }

            // Remove from sharedToUserIds if present (upgrade viewer to participant)
            const updatedSharedTo = trip.sharedToUserIds.filter((id) => id !== userId);
            const updatedParticipants = [...trip.participantIds, userId];

            await this.updateTrip(tripId, {
                participantIds: updatedParticipants,
                sharedToUserIds: updatedSharedTo,
            });

            console.log('✅ [TripService] Participant added successfully');
        } catch (error) {
            console.error('❌ [TripService] Error adding participant:', error);
            throw error;
        }
    }

    // Add viewer to trip
    static async addViewer(tripId: string, userId: string): Promise<void> {
        try {
            console.log('👁 [TripService] Adding viewer:', userId, 'to trip:', tripId);
            const trip = await this.getTripById(tripId);
            if (!trip) throw new Error('Trip not found');

            // Check if user is already a viewer or participant
            if (trip.sharedToUserIds.includes(userId) || trip.participantIds.includes(userId)) {
                console.log('⚠️ User is already a viewer or participant');
                return;
            }

            const updatedSharedTo = [...trip.sharedToUserIds, userId];

            await this.updateTrip(tripId, {
                sharedToUserIds: updatedSharedTo,
            });

            console.log('✅ [TripService] Viewer added successfully');
        } catch (error) {
            console.error('❌ [TripService] Error adding viewer:', error);
            throw error;
        }
    }

    // Change user role (participant <-> viewer)
    static async changeUserRole(
        tripId: string,
        userId: string,
        newRole: 'participant' | 'viewer'
    ): Promise<void> {
        try {
            console.log('🔄 [TripService] Changing role for user:', userId, 'to:', newRole);
            const trip = await this.getTripById(tripId);
            if (!trip) throw new Error('Trip not found');

            if (newRole === 'participant') {
                // Move from sharedToUserIds to participantIds
                const updatedSharedTo = trip.sharedToUserIds.filter((id) => id !== userId);
                const updatedParticipants = trip.participantIds.includes(userId)
                    ? trip.participantIds
                    : [...trip.participantIds, userId];

                await this.updateTrip(tripId, {
                    participantIds: updatedParticipants,
                    sharedToUserIds: updatedSharedTo,
                });
            } else {
                // Move from participantIds to sharedToUserIds
                const updatedParticipants = trip.participantIds.filter((id) => id !== userId);
                const updatedSharedTo = trip.sharedToUserIds.includes(userId)
                    ? trip.sharedToUserIds
                    : [...trip.sharedToUserIds, userId];

                await this.updateTrip(tripId, {
                    participantIds: updatedParticipants,
                    sharedToUserIds: updatedSharedTo,
                });
            }

            console.log('✅ [TripService] User role changed successfully');
        } catch (error) {
            console.error('❌ [TripService] Error changing user role:', error);
            throw error;
        }
    }

    // Remove user from trip (participant or viewer)
    static async removeUser(tripId: string, userId: string): Promise<void> {
        try {
            console.log('🗑 [TripService] Removing user:', userId, 'from trip:', tripId);
            // Use atomic arrayRemove — avoids read-then-write race conditions and
            // satisfies the Firestore rule that only allows participantIds/sharedToUserIds/updatedAt.
            const tripRef = doc(db, this.COLLECTION, tripId);
            await updateDoc(tripRef, {
                participantIds: arrayRemove(userId),
                sharedToUserIds: arrayRemove(userId),
                updatedAt: serverTimestamp(),
            });
            console.log('✅ [TripService] User removed successfully:', userId);
        } catch (error) {
            console.error('❌ [TripService] Error removing user:', error);
            throw error;
        }
    }

    // Archive a trip (per-user)
    static async archiveTrip(tripId: string, userId: string): Promise<void> {
        try {
            console.log('📦 [TripService] Archiving trip:', tripId, 'for user:', userId);
            const tripRef = doc(db, this.COLLECTION, tripId);
            await updateDoc(tripRef, {
                archivedByUsers: arrayUnion(userId),
                updatedAt: serverTimestamp(),
            });
            console.log('✅ [TripService] Trip archived successfully');
        } catch (error) {
            console.error('❌ [TripService] Error archiving trip:', error);
            throw error;
        }
    }

    // Unarchive a trip (per-user)
    static async unarchiveTrip(tripId: string, userId: string): Promise<void> {
        try {
            console.log('📂 [TripService] Unarchiving trip:', tripId, 'for user:', userId);
            const tripRef = doc(db, this.COLLECTION, tripId);
            await updateDoc(tripRef, {
                archivedByUsers: arrayRemove(userId),
                updatedAt: serverTimestamp(),
            });
            console.log('✅ [TripService] Trip unarchived successfully');
        } catch (error) {
            console.error('❌ [TripService] Error unarchiving trip:', error);
            throw error;
        }
    }

    // Duplicate a trip
    /**
     * Duplicates a trip the current user can already read (owner/participant/
     * viewer) into a new trip they own — including its activities,
     * accommodations, flights and car rentals. Expenses, documents and
     * participants are intentionally not copied.
     *
     * [nameSuffix] is appended to the name (e.g. "Norway 2024 [Copy]"); falls
     * back to "(Copy)" when not provided.
     */
    static async duplicateTrip(tripId: string, userId: string, nameSuffix?: string): Promise<string> {
        try {
            console.log('📋 [TripService] Duplicating trip:', tripId);
            const originalTrip = await this.getTripById(tripId);
            if (!originalTrip) {
                throw new Error('Trip not found');
            }

            const suffix = nameSuffix && nameSuffix.trim().length > 0 ? nameSuffix.trim() : '(Copy)';

            // Create new trip with copied data
            const newTripData = {
                name: `${originalTrip.name} ${suffix}`,
                ownerId: userId,
                startDate: originalTrip.startDate,
                endDate: originalTrip.endDate,
                participantIds: [userId], // Only the duplicator is a participant
                sharedToUserIds: [],
                countries: originalTrip.countries,
                mainCountry: originalTrip.mainCountry,
                regions: originalTrip.regions,
                highlightImages: originalTrip.highlightImages,
                mainImageIndex: originalTrip.mainImageIndex,
                map: originalTrip.map,
                defaultCurrency: originalTrip.defaultCurrency,
                currencyRates: originalTrip.currencyRates,
                mapNeedsUpdate: originalTrip.mapNeedsUpdate,
                archivedByUsers: [],
            };

            const newTripId = await this.createTrip(newTripData);

            // Copy the trip's content subcollections into the new trip.
            await this.copyTripSubcollections(tripId, newTripId);

            console.log('✅ [TripService] Trip duplicated successfully. New ID:', newTripId);
            return newTripId;
        } catch (error) {
            console.error('❌ [TripService] Error duplicating trip:', error);
            throw error;
        }
    }

    /**
     * Copies the content subcollections (activities, accommodations,
     * flightTickets, carRentals) from one trip to another, regenerating each
     * doc id and repointing any internal `tripId` field to the destination.
     * The caller must have read access to [srcTripId].
     */
    private static async copyTripSubcollections(srcTripId: string, destTripId: string): Promise<void> {
        const subs = ['activities', 'accommodations', 'flightTickets', 'carRentals'];
        for (const sub of subs) {
            const srcSnap = await getDocs(collection(db, 'trips', srcTripId, sub));
            if (srcSnap.empty) continue;

            let batch = writeBatch(db);
            let ops = 0;
            for (const d of srcSnap.docs) {
                const data: Record<string, unknown> = { ...d.data() };
                delete data.id;
                if ('tripId' in data) data.tripId = destTripId;
                data.createdAt = serverTimestamp();
                data.updatedAt = serverTimestamp();
                const destRef = doc(collection(db, 'trips', destTripId, sub));
                batch.set(destRef, data);
                ops++;
                if (ops >= 400) {
                    await batch.commit();
                    batch = writeBatch(db);
                    ops = 0;
                }
            }
            if (ops > 0) await batch.commit();
        }
    }

    /**
     * Redeems a clone-mode share token: returns an independent owned COPY of
     * the shared trip. Runs server-side (Cloud Function) because the recipient
     * has no client read access to someone else's trip.
     *
     * [code] is the share code (the `tripShareTokens` doc id). [nameSuffix] is
     * appended to the cloned trip name (e.g. "[Copy]"). Returns the new trip id.
     * Throws with code 'resource-exhausted' when the user hit their trip limit.
     *
     * The copy is the itinerary only — activities and accommodations. [options]
     * carries the user's choices: the function re-bases every date onto
     * `newStartDate` and drops the original owner's prices unless `keepPrices`.
     */
    static async redeemCloneToken(
        code: string,
        nameSuffix?: string,
        options?: CloneOptions,
    ): Promise<string> {
        const callable = httpsCallable<
            { code: string; nameSuffix?: string; newStartDate?: string; keepPrices: boolean },
            { tripId: string }
        >(functions, 'cloneSharedTrip');
        const result = await callable({ code, nameSuffix, ...cloneOptionsPayload(options) });
        const tripId = result.data?.tripId;
        if (!tripId) {
            throw new Error('Clone failed: no trip id returned');
        }
        return tripId;
    }

    /**
     * Redeems a share token as a read-only VIEWER: adds the caller to the
     * trip's `sharedToUserIds` so it appears in their trip list. Runs
     * server-side (Cloud Function) because Firestore rules forbid a non-member
     * from adding themselves as a viewer client-side. View links are reusable
     * (not consumed). Returns the shared trip id.
     */
    static async redeemViewToken(code: string): Promise<string> {
        const callable = httpsCallable<{ code: string }, { tripId: string }>(
            functions,
            'redeemViewToken',
        );
        const result = await callable({ code });
        const tripId = result.data?.tripId;
        if (!tripId) {
            throw new Error('View redeem failed: no trip id returned');
        }
        return tripId;
    }

    /**
     * Creates a consent-based redeem link (mobile-style share token) that lets
     * the recipient self-join the trip in the chosen [mode] — 'view' (read-only
     * viewer), 'participant' (editor), or 'clone' (own copy). Mirrors the mobile
     * `createTripShareToken`: the 6-char doc id is the code, and a companion
     * read-only view-share is registered so the recipient can preview the trip
     * on the web before deciding.
     *
     * Returns the code and the full `/redeem/{code}` URL to share.
     */
    static async createRedeemToken(
        tripId: string,
        mode: 'view' | 'participant' | 'clone',
        tripName: string,
    ): Promise<{ code: string; url: string }> {
        const user = auth.currentUser;
        if (!user) throw new Error('Not signed in');

        // Unambiguous 6-char code (mirrors mobile AppHelper.generateShareCode).
        const genCode = (): string => {
            const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
            let out = '';
            for (let i = 0; i < 6; i++) out += chars.charAt(Math.floor(Math.random() * chars.length));
            return out;
        };
        let code = genCode();
        for (let i = 0; i < 5; i++) {
            const existing = await getDoc(doc(db, 'tripShareTokens', code));
            if (!existing.exists()) break;
            code = genCode();
        }

        // Companion read-only view-share for the web preview (best-effort).
        let viewToken: string | null = null;
        try {
            const vt = Array.from({ length: 32 }, () =>
                'abcdefghijklmnopqrstuvwxyz0123456789'.charAt(Math.floor(Math.random() * 36))).join('');
            await setDoc(doc(collection(db, 'tripShareTokens')), {
                tripId,
                token: vt,
                createdBy: user.uid,
                viewCount: 0,
                isActive: true,
                allowEdit: false,
                requiresAuth: false,
                expiresAt: Timestamp.fromDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)),
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });
            await setDoc(doc(db, 'tripPublicShares', tripId), { isPublic: true });
            viewToken = vt;
        } catch (e) {
            console.error('Failed to create view-share for redeem link:', e);
        }

        await setDoc(doc(db, 'tripShareTokens', code), {
            tripId,
            ownerId: user.uid,
            creatorUserId: user.uid,
            createdAt: serverTimestamp(),
            used: false,
            redeemedByUserId: null,
            expiresAt: null,
            redeemedAt: null,
            mode,
            tripName,
            viewToken,
        });

        return { code, url: `${window.location.origin}/redeem/${code}` };
    }

    /**
     * Redeems a participant-mode share token: adds the caller to the trip's
     * `participantIds` as an editing collaborator. Runs server-side (Cloud
     * Function) because Firestore rules forbid a non-member from adding
     * themselves as a participant. The function enforces the trip owner's
     * participant tier limit and throws with code 'resource-exhausted' when it
     * is exceeded. Returns the shared trip id.
     */
    static async redeemParticipantToken(code: string): Promise<string> {
        const callable = httpsCallable<{ code: string }, { tripId: string }>(
            functions,
            'redeemParticipantToken',
        );
        const result = await callable({ code });
        const tripId = result.data?.tripId;
        if (!tripId) {
            throw new Error('Participant redeem failed: no trip id returned');
        }
        return tripId;
    }

    // Categorize trips by status (excludes archived trips for the given user)
    static categorizeTripsByStatus(trips: Trip[], currentUserId?: string): {
        inProgress: Trip[];
        upcoming: Trip[];
        previous: Trip[];
    } {
        const now = new Date();
        const todayNorm = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const inProgress: Trip[] = [];
        const upcoming: Trip[] = [];
        const previous: Trip[] = [];

        trips.forEach((trip) => {
            // Skip trips archived by this user
            if (currentUserId && trip.archivedByUsers?.includes(currentUserId)) return;
            // Backwards compat: skip legacy isArchived=true trips
            if (!currentUserId && trip.isArchived) return;

            const s = new Date(trip.startDate);
            const tripStart = new Date(s.getFullYear(), s.getMonth(), s.getDate());
            const e = new Date(trip.endDate);
            const tripEnd = new Date(e.getFullYear(), e.getMonth(), e.getDate());

            if (todayNorm >= tripStart && todayNorm <= tripEnd) {
                inProgress.push(trip);
            } else if (todayNorm < tripStart) {
                upcoming.push(trip);
            } else {
                previous.push(trip);
            }
        });

        // Upcoming/in-progress: soonest first. Previous: most recent first.
        const byStartDate = (a: Trip, b: Trip) =>
            new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
        inProgress.sort(byStartDate);
        upcoming.sort(byStartDate);
        previous.sort((a, b) => byStartDate(b, a));

        return { inProgress, upcoming, previous };
    }
}

export default TripService;
